/**
 * useFieldErrors Hook - Field-Specific Error Handling
 * 
 * Manages field-specific validation errors from backend ValidationError responses
 * Supports the enhanced API error format from Phase 4 with field paths and error codes
 * 
 * Features:
 * - Field-specific error mapping and display
 * - General error handling for non-field errors
 * - Auto-clearing errors on field focus/change
 * - Error code mapping for user-friendly messages
 * - Batch error handling for multiple field errors
 * - Structured `{ error: { message, code, field } }` (discount CRUD) maps to the field
 * 
 * Usage:
 * const { fieldErrors, generalErrors, handleApiErrors, clearFieldError } = useFieldErrors();
 */

import { useState, useCallback } from 'react';
import logger from '../utils/logger';

/**
 * Custom hook for managing field-specific validation errors
 * 
 * @returns {Object} Error state and management functions
 */
export const useFieldErrors = () => {
  // Field-specific errors: { fieldName: { message, code } }
  const [fieldErrors, setFieldErrors] = useState({});
  
  // General errors (no specific field): [{ message, code }]
  const [generalErrors, setGeneralErrors] = useState([]);

  /**
   * Clear all errors
   */
  const clearErrors = useCallback(() => {
    setFieldErrors({});
    setGeneralErrors([]);
  }, []);

  /**
   * Clear error for specific field
   * @param {string} fieldName - Name of field to clear error for
   */
  const clearFieldError = useCallback((fieldName) => {
    setFieldErrors(prev => {
      const { [fieldName]: removed, ...rest } = prev;
      return rest;
    });
  }, []);

  /**
   * Set error for specific field
   * @param {string} fieldName - Name of field
   * @param {string} message - Error message
   * @param {string} code - Error code
   */
  const setFieldError = useCallback((fieldName, message, code = 'VALIDATION_ERROR') => {
    setFieldErrors(prev => ({
      ...prev,
      [fieldName]: { message, code }
    }));
  }, []);

  /**
   * Add general error (not field-specific)
   * @param {string} message - Error message
   * @param {string} code - Error code
   */
  const addGeneralError = useCallback((message, code = 'GENERAL_ERROR') => {
    setGeneralErrors(prev => [...prev, { message, code }]);
  }, []);

  /**
   * Map error codes to user-friendly messages
   * @param {string} code - Backend error code
   * @param {string} field - Field name
   * @param {string} originalMessage - Original message from backend
   * @returns {string} User-friendly error message
   */
  const getErrorMessage = useCallback((code, field, originalMessage) => {
    const fieldDisplayNames = {
      'customer_id': 'Customer',
      'tax_rate': 'Tax Rate',
      'ship_to_postal_code': 'Shipping Postal Code',
      'merchant_display_postal_code': 'Merchant Postal Code',
      'payment_method': 'Payment Method',
      'discount_value': 'Discount Amount',
      'discount_type': 'Discount Type',
      'shipping_cost': 'Shipping Cost',
      'items': 'Line Items'
    };

    const fieldName = fieldDisplayNames[field] || field;

    const messageTemplates = {
      'REQUIRED': `${fieldName} is required`,
      'INVALID_TYPE': `${fieldName} must be a valid value`,
      'INVALID_RANGE': `${fieldName} must be within the valid range`,
      'INVALID_FORMAT': `${fieldName} format is not valid`,
      'INVALID_ENUM': `${fieldName} value is not allowed`,
      'BUSINESS_RULE_VIOLATION': `${fieldName}: ${originalMessage}`,
      'CONSTRAINT_VIOLATION': `${fieldName} violates business rules`,
      'INSUFFICIENT_DATA': `${fieldName} requires additional information`
    };

    return messageTemplates[code] || originalMessage || `${fieldName} validation error`;
  }, []);

  /**
   * Handle API errors from enhanced backend error format
   * Processes ValidationError responses with field-specific details
   * 
   * @param {Error} error - API error object
   */
  const handleApiErrors = useCallback((error) => {
    logger.debug('Processing API errors', {
      operation: 'useFieldErrors.handleApiErrors',
      status: error.response?.status,
      hasErrors: !!error.response?.data?.errors
    });

    // Clear existing errors first
    clearErrors();

    // Check for enhanced error format from Phase 4
    if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
      const newFieldErrors = {};
      const newGeneralErrors = [];

      error.response.data.errors.forEach(err => {
        if (err.field) {
          // Field-specific error
          const friendlyMessage = getErrorMessage(err.code, err.field, err.message);
          newFieldErrors[err.field] = {
            message: friendlyMessage,
            code: err.code || 'VALIDATION_ERROR',
            originalMessage: err.message
          };
          
          logger.debug('Field-specific error processed', {
            field: err.field,
            code: err.code,
            message: friendlyMessage
          });
        } else {
          // General error
          newGeneralErrors.push({
            message: err.message || 'An error occurred',
            code: err.code || 'GENERAL_ERROR'
          });
          
          logger.debug('General error processed', {
            code: err.code,
            message: err.message
          });
        }
      });

      setFieldErrors(newFieldErrors);
      setGeneralErrors(newGeneralErrors);

      logger.info('API errors processed', {
        operation: 'useFieldErrors.handleApiErrors',
        fieldErrorCount: Object.keys(newFieldErrors).length,
        generalErrorCount: newGeneralErrors.length
      });

    } else if (error.response?.data?.error) {
      const payload = error.response.data.error;
      if (payload && typeof payload === 'object') {
        const message = typeof payload.message === 'string' && payload.message
          ? payload.message
          : 'Check the highlighted fields and try again.';
        const code = payload.code || 'LEGACY_ERROR';
        if (payload.field) {
          setFieldErrors({
            [payload.field]: {
              message,
              code,
              originalMessage: payload.message
            }
          });
        } else {
          addGeneralError(message, code);
        }
      } else {
        addGeneralError(payload, 'LEGACY_ERROR');
      }
    } else if (error.message) {
      // Network or other error
      addGeneralError(error.message, 'NETWORK_ERROR');
    } else {
      // Unknown error
      addGeneralError('An unexpected error occurred', 'UNKNOWN_ERROR');
    }
  }, [clearErrors, getErrorMessage, addGeneralError]);

  /**
   * Handle form validation errors (client-side)
   * @param {Object} validationErrors - Object with field: message pairs
   */
  const handleValidationErrors = useCallback((validationErrors) => {
    const newFieldErrors = {};
    
    Object.entries(validationErrors).forEach(([field, message]) => {
      newFieldErrors[field] = {
        message,
        code: 'CLIENT_VALIDATION',
        originalMessage: message
      };
    });
    
    setFieldErrors(prev => ({ ...prev, ...newFieldErrors }));
  }, []);

  /**
   * Check if specific field has error
   * @param {string} fieldName - Field name to check
   * @returns {boolean} True if field has error
   */
  const hasFieldError = useCallback((fieldName) => {
    return !!fieldErrors[fieldName];
  }, [fieldErrors]);

  /**
   * Get error for specific field
   * @param {string} fieldName - Field name
   * @returns {Object|null} Error object or null
   */
  const getFieldError = useCallback((fieldName) => {
    return fieldErrors[fieldName] || null;
  }, [fieldErrors]);

  /**
   * Get CSS classes for field based on error state
   * @param {string} fieldName - Field name
   * @param {string} baseClasses - Base CSS classes
   * @returns {string} Combined CSS classes
   */
  const getFieldClasses = useCallback((fieldName, baseClasses = '') => {
    const hasError = hasFieldError(fieldName);
    const errorClasses = hasError ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500';
    return `${baseClasses} ${errorClasses}`.trim();
  }, [hasFieldError]);

  /**
   * Get error count for display purposes
   * @returns {Object} Error counts
   */
  const getErrorCounts = useCallback(() => {
    return {
      fieldErrors: Object.keys(fieldErrors).length,
      generalErrors: generalErrors.length,
      total: Object.keys(fieldErrors).length + generalErrors.length
    };
  }, [fieldErrors, generalErrors]);

  // Return hook interface
  return {
    // Error state
    fieldErrors,
    generalErrors,
    
    // Error management
    clearErrors,
    clearFieldError,
    setFieldError,
    addGeneralError,
    
    // Error handling
    handleApiErrors,
    handleValidationErrors,
    
    // Error checking
    hasFieldError,
    getFieldError,
    hasErrors: Object.keys(fieldErrors).length > 0 || generalErrors.length > 0,
    
    // UI helpers
    getFieldClasses,
    getErrorCounts,
    
    // Metadata
    errorMapping: {
      fieldErrorCount: Object.keys(fieldErrors).length,
      generalErrorCount: generalErrors.length,
      lastProcessed: generalErrors.length > 0 || Object.keys(fieldErrors).length > 0 ? Date.now() : null
    }
  };
};

export default useFieldErrors;